import _isString from 'lodash/isString';
import _has from 'lodash/has';
import Cookies from 'universal-cookie';
import { getContent } from 'pp-react-l10n';
import * as community from '../../data/community.json';
import * as topicLabels from '../../data/topicLabels.json';
import { PageMapping } from './page-name-identifiers';

const html = document.querySelector('html');
const cobrand = html.getAttribute('cobrand') || 'us';
const locale = html.getAttribute('lang') || 'en-US';
const language = html.getAttribute('language') || 'en';
const device = html.getAttribute('data-device-type');
const browserType = document.body.getAttribute('data-browser-type');
const isSmcEnabled = html.getAttribute('isSmcEnabled');
const pageName = html.getAttribute('page') || '';
const isHeroModuleNoLink = html.getAttribute('isHeroModuleNoLink');
const isIDIContactOptions = html.getAttribute('isIDIContactOptions');
const isTechnicalTabEnable = html.getAttribute('isTechnicalTabEnable');
const userExperience = html.getAttribute('experience');
const isNewSearchExperience = html.hasAttribute('is-new-search-experience') || false;
const isBusinessUser = html.hasAttribute('is-business-user') || false;
const contentGlobalContainer = document.getElementsByClassName('vx_foreground-container');
const isToggleTreatment = html.hasAttribute('isToggleTreatment');
const regexMTS = /^(TS|ts)\d+$/;
const from8BallFooter = html.getAttribute('isFrom8BallFooter');
const isComplaintEnabledMarket = html.getAttribute('isComplaintEnabledMarket');
const USBusinessUser = html.getAttribute('isUSBusinessUser');
const direction = html.getAttribute('dir') || 'ltr';

const cookies = new Cookies();

const warn = function warn(message) {
  console.error(`☃ ${message}`); // eslint-disable-line no-console
};

// Check basic elements required for the app
(function pageInitCheck() {
  // locale bundle
  if (!window.localeBundle) {
    warn('Missing locale bundle!');
  }

  // user profile
  if (!window.userProfile) {
    warn('Missing user profile!');
  }
}());

const Page = {
  checkPageInPath(path) {
    // hard code route path for check
    // Check home route
    const validatePages = ['home'];
    let validPath = false;
    validatePages.forEach((page) => {
      if (path.indexOf(page) >= 0) {
        validPath = true;
      }
    });
    return validPath;
  },

  getCountry() {
    return cobrand;
  },

  getBaseUrl() {
    if (window.location.href.includes('localhost')) {
      return `https://${window.location.host}`;
    }
    return `https://${window.location.hostname}`;
  },

  isHomePage() {
    return (pageName === 'home');
  },

  getPageName() {
    return pageName;
  },

  getCookie(name) {
    return cookies.get(name);
  },

  setCookie(name, value, cookieOptions) {
    const options = Object.assign({}, cookieOptions);
    cookies.set(name, value, options);
  },

  getLocaleContent(file) {
    const content = window.localeBundle && window.localeBundle[`smarthelp/${file}`];

    if (!content) {
      warn(`Property file smarthelp/${file} missing`);
    }
    return content;
  },

  getLocaleJson() {
    return window.localeBundle;
  },

  getLocale() {
    return locale;
  },

  getLanguage() {
    return language;
  },

  getUserProfile() {
    return window.userProfile;
  },

  getExpByUserType() {
    const experience = userExperience || ((isBusinessUser) ? 'business' : 'personal');
    return experience;
  },

  getBaseURLForSearchExperience() {
    let baseUrl = `${cobrand}/selfhelp`;
    if (isNewSearchExperience) {
      baseUrl = `${cobrand}/smarthelp`;
    }
    return baseUrl;
  },

  getBaseURLWithExperience() {
    return `${cobrand}/smarthelp`;
  },

  getIsSmcEnabled() {
    return isSmcEnabled === 'true';
  },

  getIsHeroModuleNoLink() {
    return isHeroModuleNoLink === 'true';
  },

  getIsIDIContactOptions() {
    return isIDIContactOptions === 'true';
  },

  getDirection() {
    return direction;
  },

  isNotIEbrowser() {
    return !(browserType === 'IE');
  },

  isNewSearchExperience() {
    return isNewSearchExperience;
  },

  isToggleTreatment() {
    return isToggleTreatment;
  },
  getIsBusinessUser() {
    return isBusinessUser;
  },
  isUSBusinessUser() {
    return USBusinessUser;
  },

  isMobileLayout() {
    return device === 'mobile';
  },

  isTabletLayout() {
    return device === 'tablet';
  },
  isXsLayout() {
    return document.body.clientWidth <= 768;
  },
  isMobileModal() {
    return (this.isMobileLayout() || this.isTabletLayout()) && this.isXsLayout();
  },

  isDesktopLayout() {
    return device === 'desktop';
  },

  isMTSArticle(articleId) {
    return regexMTS.test(articleId);
  },

  isLoggedIn() {
    return document.body.getAttribute('isloggedin');
  },

  // For PPC logout part checker
  isLogoutUS() {
    return this.getCountry() === 'us' && !this.isLoggedIn();
  },

  getCSRFtoken() {
    return document.body.getAttribute('data-token');
  },

  getDeviceType() {
    return document.body.getAttribute('data-device-type');
  },

  // TODO: We are formatting the title and append the article id later in all the palces where this function is used
  //       So we can simply refactor this code and use getFormattedUrlForArticle passing both articleTitle and articleId
  getFaqUrlFromTitle(title) {
    return title && title.replace(/ /g, '-').replace(/\?/g, '').replace(/[/\\]/g, '-').toLowerCase();
  },

  getFormattedUrlForArticle(title, articleId) {
    return encodeURIComponent(`${this.getFaqUrlFromTitle(title)}-${articleId.toLowerCase()}`);
  },

  getFullArticleURL(currentArticle) {
    return currentArticle && currentArticle.articleId ?
    `/${this.getBaseURLWithExperience()}/article/${this.getFormattedUrlForArticle(currentArticle.articleTitle, currentArticle.articleId)}`
    : '';
  },

  // Get HCR Home page URL
  getHomeURL() {
    return `/${this.getBaseURLWithExperience()}/home`;
  },

  // Get HCR Topic page URL
  getTopicURL(topic) {
    return (topic && topic.topicId) ?
    `/${this.getBaseURLWithExperience()}/topic/${topic.topicId}` : '';
  },

  getUserExperience() {
    return userExperience;
  },

  isTechnicalExp() {
    return Page.getUserExperience() === 'technical';
  },

  // Using remote config to control UI technical tab/exp
  hasTechnicalTab() {
    return isTechnicalTabEnable === 'true';
  },

  isComplaintEnabledMarket() {
    return isComplaintEnabledMarket;
  },
  getPathName() {
    return window.location.pathname;
  },

  goTo(url) {
    const referLink = document.createElement('a');
    referLink.href = url;
    document.body.appendChild(referLink);
    referLink.click();
  },

  getCommunityLocale() {
    return `${Page.getLanguage()}-${Page.getCountry().toUpperCase()}`;
  },

  getCommunityLink() {
    if (this.isTechnicalExp()) {
      return community.community.technical;
    }
    return community.community[Page.getCommunityLocale()];
  },

  getCommunitySearchURL() {
    const validMarket = Object.keys(community.community);
    if (validMarket.indexOf(Page.getCommunityLocale()) >= 0) {
      return community.searchURL;
    }
    return null;
  },

  getGlobalContainer() {
    return contentGlobalContainer;
  },

  getTreatments() {
    return window.applicableTreatments;
  },

  getEligibleFilters() {
    return window.eligibleFilters;
  },

  getTopicLabels() {
    return topicLabels.topics[Page.getLocale()];
  },

  getOriginPath() {
    return window.location.origin;
  },

  setDocumentTitle(title) {
    window.document.title = title;
  },
  setCanonicalTag(options) {
    // avoid duplicate canonical URL's & update the href 1 : 1 mapping
    const allElem = document.getElementsByTagName('link');
    for (let i = 0; i < allElem.length; i += 1) {
      if (allElem[i].getAttribute('rel') === 'canonical') {
        if (options.name === 'home') {
          allElem[i].setAttribute('href', this.getHomePageUrlForSelfhelp());
          return;
        } else if (options.name === 'article') {
          allElem[i].setAttribute('href', this.getArticleUrlForSelfhelp(options.id, options.title));
          return;
        }
      }
    }
    // construct the helpcenter URL based on home / article page.
    const url = options.name === 'home' ? this.getHomePageUrlForSelfhelp() : this.getArticleUrlForSelfhelp(options.id, options.title);
    const canonicalTag = document.createElement('link');
    canonicalTag.rel = 'canonical';
    canonicalTag.href = url;
    document.getElementsByTagName('head')[0].appendChild(canonicalTag);
  },
  // function to create the article URL to match the existing helpcenter for SEO purpose
  getArticleUrlForSelfhelp(faqId, title) {
    const origin = this.getOriginPath();
    const faqLowercase = faqId && faqId.toLowerCase();
    const replaceTitle = title ? `${this.getFaqUrlFromTitle(title)}-` : '';
    const baseUrl = `${origin}/${cobrand}/selfhelp/article/${replaceTitle}${faqLowercase}`;
    return baseUrl;
  },
  getHomePageUrlForSelfhelp() {
    const origin = this.getOriginPath();
    return `${origin}/${cobrand}/selfhelp/home`;
  },
  // TODO: using PXP to switch article link in the HCR
  // TODO: add rank into the article link
  getArticleLink(faqId, faqTitle = '') {
    const country = this.getCountry();
    const articleTitle = faqTitle ? `${this.getFaqUrlFromTitle(faqTitle)}-` : '';
    return `/${country}/smarthelp/article/${articleTitle}${faqId.toLowerCase()}`;
  },

  getSanitizedValue(value) {
    const returnValue = value ? value.trim() : '';
    try {
      return encodeURIComponent(returnValue);
    } catch (error) {
      return returnValue;
    }
  },

  // Get FPTI page name id by path mapping the indentifer file
  getFPTIPageId() {
    let tempPath = '';
    const locationPath = window.location && window.location.pathname;
    if (locationPath && _isString(locationPath)) {
      const temp = locationPath.split('/'); // Split path by '/'
      if (Array.isArray(temp) && _has(PageMapping, temp[3])) {
        tempPath = temp[3];
      }
    }
    return PageMapping[tempPath] || 'homePage';
  },

  isTopicTreeDisabled() {
    return (document.querySelector('html') && document.querySelector('html').hasAttribute('topic_tree_disabled')) || false;
  },

  // Using L10N html to replace dangerouslySetInnerHTML
  getHTMLElement(options) {
    return getContent({
      content: {
        innerMessage: options.message,
      },
      path: 'innerMessage',
      useHTML: options.useHTML || false,
    });
  },

  // Scroll Screan View for fixed header
  scrollDown(headerHeigh = 75) {
    const scrolledY = window.scrollY;
    if (scrolledY) {
      window.scroll(0, scrolledY - headerHeigh);
    }
  },

  scrollToTop() {
    setTimeout(() => {
      window.scroll(0, 0);
    }, 0);
  },
  /**
   * Setting home page redirect URL with user selected Exp query
   * options: {
   *  path,
   *  search,
   *  experience,
   * }
   */
  getSelectedExpURL(options) {
    // TODO: keep search query
    return `${options.path}?selectedExp=${options.experience}`;
  },

  formalQuickAnswerKey({ layer, topicId }) {
    return `${layer}|${topicId}`;
  },
  /**
   * Forced intent condition:
   * 1) The country is not US
   * 2) The users are not coming from 8 ball footer for US consumer
   * 3) OR Reach to the TOPIC leaf node
   * 4) and not reach to email page
   * @return Boolean   display contact option
   */
  isValidToDisplayContactOptions(selectResult = false, emailSelect = false) {
    return (this.isTopicTreeDisabled() || ((from8BallFooter === 'true') || !this.isLoggedIn() || isBusinessUser || selectResult)) && !emailSelect;
  },
  getEmailThankYouPageDescription(topicRefId) {
    if (this.isComplaintEnabledMarket() && this.getIsSmcEnabled() && topicRefId === 'formal_complaints_l1') {
      return 'emailSentNotesForComplaintMarkets';
    } else if (this.isComplaintEnabledMarket() && topicRefId === 'formal_complaints_l1' && !this.getIsSmcEnabled()) {
      return 'nonSmcEmailSentNotesForComplaintMarkets';
    } else if (!this.isComplaintEnabledMarket() && this.getIsSmcEnabled()) {
      return 'emailSentNotes';
    }
    return 'nonSmcEmailSentNotes';
  },

  /**
   * Update/Add query base ame and value in to URL
   * @param {string} url    origin/target URL
   * @param {string} name   query name
   * @param {string} value  query value
   * @return {string}       Update URL
   */
  setURLQuery(url, name, value) {
    if (!url) return '';

    const queryName = name.replace(/[[\]]/g, '\\$&');

    // Check Query exist or not
    const regexQueryChecker = new RegExp('[?]([a-zA-z0-9]+)(=([^&#]*)|&|#|$)');

    if (regexQueryChecker.test(url)) {
      // exist any query, then check target query by its name
      const regexQueryValue = new RegExp(`[?&]${queryName}(=([^&#]*)|&|#|$)`);
      const results = regexQueryValue.exec(url);

      if (regexQueryValue.test(url) && Array.isArray(results) && results.length > 0) {
        const replacement = `${queryName}=${value}`;
        return url.replace(`${queryName}${results[1]}`, replacement);
      }
      return `${url}&${queryName}=${value}`;
    }
    // no query
    return `${url}?${queryName}=${value}`;
  },

  // Set `mts` value to `channel` query into URL
  setChannelForTechnicalExp(url) {
    const CHANNEL = 'channel';
    const TECHNICALEXP = 'mts';

    if (Page.isTechnicalExp()) {
      return Page.setURLQuery(url, CHANNEL, TECHNICALEXP);
    }
    return url;
  },
};

export default Page;
